import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { apiService } from "../../services/apiService";

export const fetchTyreData = createAsyncThunk(
  "maintenance/fetchTyreData",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyreData(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyres");
    }
  }
);

export const fetchTyresOverview = createAsyncThunk(
  "tyre/fetchTyresOverview",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyresOverview();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyres overview");
    }
  }
);

export const createTyre = createAsyncThunk(
  "tyre/createTyre",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiService.createTyre(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to create tyre");
    }
  }
);

export const fetchTyreDetail = createAsyncThunk(
  "tyre/fetchTyreDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyreDetail(id);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyre detail");
    }
  }
);

export const fetchTyreHistory = createAsyncThunk(
  "tyre/fetchTyreHistory",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyreHistory(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyre history");
    }
  }
);

export const updateTyreGeneralInfo = createAsyncThunk(
  "tyre/updateTyreGeneralInfo",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await apiService.updateTyreGeneralInfo(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to update tyre general information");
    }
  }
);

export const fetchTyreServiceDetail = createAsyncThunk(
  "tyre/fetchTyreServiceDetail",
  async ({ tyreId, serviceId }, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyreServiceDetail(tyreId, serviceId);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyre service detail");
    }
  }
);

export const fetchTyreServiceRecords = createAsyncThunk(
  "tyre/fetchTyreServiceRecords",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyreServices(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyre service records");
    }
  }
);

export const fetchTyreTrips = createAsyncThunk(
  "tyre/fetchTyreTrips",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyreTrips(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyre trips");
    }
  }
);

export const fetchTyreAssignmentHistory = createAsyncThunk(
  "tyre/fetchTyreAssignmentHistory",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyreAssignmentHistory(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyre assignment history");
    }
  }
);

export const commentOnTyre = createAsyncThunk(
  "tyre/commentOnTyre",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiService.commentOnTyre(data);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to comment on tyre");
    }
  }
);

export const deleteTyre = createAsyncThunk(
  "tyre/deleteTyre",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.deleteTyre(id);
      return { ...response, deletedId: id };
    } catch (error) {
      return rejectWithValue(error || "Failed to delete tyre");
    }
  }
);

export const fetchTyreBrandsDropdown = createAsyncThunk(
  "tyre/fetchTyreBrandsDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyreBrandsDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyre brands");
    }
  }
);

export const fetchTyreModelsDropdown = createAsyncThunk(
  "tyre/fetchTyreModelsDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyreModelsDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyre models");
    }
  }
);

export const fetchTyrePatternsDropdown = createAsyncThunk(
  "tyre/fetchTyrePatternsDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyrePatternsDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyre patterns");
    }
  }
);

export const fetchTyreTypesDropdown = createAsyncThunk(
  "tyre/fetchTyreTypesDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyreTypesDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyre types");
    }
  }
);

export const fetchTyresDropdown = createAsyncThunk(
  "tyre/fetchTyresDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getTyresDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch tyres dropdown");
    }
  }
);

export const fetchAssetTyres = createAsyncThunk(
  "tyre/fetchAssetTyres",
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.getAssetTyres(params);
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch asset tyres");
    }
  }
);

const initialState = {
  data: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalRecords: 0,
  meta: {},
  createTyreLoading: false,
  createTyreError: null,
  createdTyreData: null,
  updateTyreGeneralInfoData: null,
  updateTyreGeneralInfoLoading: false,
  updateTyreGeneralInfoError: null,
  tyreDetailData: null,
  tyreDetailLoading: false,
  tyreDetailError: null,
  tyreServiceRecordsData: [],
  tyreServiceRecordsLoading: false,
  tyreServiceRecordsError: null,
  tyreServiceRecordsMeta: {},
  tyreTripsData: [],
  tyreTripsLoading: false,
  tyreTripsError: null,
  tyreTripsMeta: {},
  tyreAssignmentHistoryData: [],
  tyreAssignmentHistoryLoading: false,
  tyreAssignmentHistoryError: null,
  tyreAssignmentHistoryMeta: {},
  tyreHistoryData: [],
  tyreHistoryLoading: false,
  tyreHistoryError: null,
  commentOnTyreData: null,
  commentOnTyreLoading: false,
  commentOnTyreError: null,
  deleteTyreLoading: false,
  deleteTyreError: null,
  tyreBrandsDropdownData: [],
  tyreModelsDropdownData: [],
  tyrePatternsDropdownData: [],
  tyreTypesDropdownData: [],
  tyresDropdownData: null,
  assetTyresData: [],
  assetTyresLoading: false,
  assetTyresError: null,
  assetTyresRequest: null,
  dropdownLoadingCount: 0,
  dropdownError: null,
  tyreOverviewData: null,
  tyreOverviewLoading: false,
  tyreOverviewError: null,
};

const tyreSlice = createSlice({
  name: "tyre",
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    clearError: (state) => {
      state.error = null;
      state.createTyreError = null;
      state.updateTyreGeneralInfoError = null;
      state.dropdownError = null;
    },
    clearCreateTyreState: (state) => {
      state.createTyreLoading = false;
      state.createTyreError = null;
      state.createdTyreData = null;
    },
    clearTyreDetailState: (state) => {
      state.updateTyreGeneralInfoData = null;
      state.updateTyreGeneralInfoLoading = false;
      state.updateTyreGeneralInfoError = null;
      state.tyreDetailData = null;
      state.tyreDetailLoading = false;
      state.tyreDetailError = null;
      state.tyreServiceRecordsData = [];
      state.tyreServiceRecordsLoading = false;
      state.tyreServiceRecordsError = null;
      state.tyreServiceRecordsMeta = {};
      state.tyreTripsData = [];
      state.tyreTripsLoading = false;
      state.tyreTripsError = null;
      state.tyreTripsMeta = {};
      state.tyreAssignmentHistoryData = [];
      state.tyreAssignmentHistoryLoading = false;
      state.tyreAssignmentHistoryError = null;
      state.tyreAssignmentHistoryMeta = {};
    },
    clearTyreHistoryState: (state) => {
      state.tyreHistoryData = [];
      state.tyreHistoryLoading = false;
      state.tyreHistoryError = null;
      state.commentOnTyreData = null;
      state.commentOnTyreLoading = false;
      state.commentOnTyreError = null;
    },
    clearTyreDropdowns: (state) => {
      state.tyreBrandsDropdownData = [];
      state.tyreModelsDropdownData = [];
      state.tyrePatternsDropdownData = [];
      state.tyreTypesDropdownData = [];
      state.tyresDropdownData = null;
      state.assetTyresData = [];
      state.assetTyresLoading = false;
      state.assetTyresError = null;
      state.assetTyresRequest = null;
      state.dropdownLoadingCount = 0;
      state.dropdownError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTyreData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTyreData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload?.data?.results || [];
        state.meta = action.payload?.data?.meta || {};
        state.totalRecords = action.payload?.data?.meta?.totalItems || 0;
        state.totalPages = action.payload?.data?.meta?.totalPages || 1;
        state.error = null;
      })
      .addCase(fetchTyreData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = [];
      })
      .addCase(fetchTyresOverview.pending, (state) => {
        state.tyreOverviewLoading = true;
        state.tyreOverviewError = null;
      })
      .addCase(fetchTyresOverview.fulfilled, (state, action) => {
        state.tyreOverviewLoading = false;
        state.tyreOverviewData = action.payload?.data || null;
        state.tyreOverviewError = null;
      })
      .addCase(fetchTyresOverview.rejected, (state, action) => {
        state.tyreOverviewLoading = false;
        state.tyreOverviewData = null;
        state.tyreOverviewError = action.payload;
      })
      .addCase(createTyre.pending, (state) => {
        state.createTyreLoading = true;
        state.createTyreError = null;
      })
      .addCase(createTyre.fulfilled, (state, action) => {
        state.createTyreLoading = false;
        state.createTyreError = null;
        state.createdTyreData = action.payload?.data || action.payload || null;
      })
      .addCase(createTyre.rejected, (state, action) => {
        state.createTyreLoading = false;
        state.createTyreError = action.payload;
      })
      .addCase(updateTyreGeneralInfo.pending, (state) => {
        state.updateTyreGeneralInfoLoading = true;
        state.updateTyreGeneralInfoError = null;
      })
      .addCase(updateTyreGeneralInfo.fulfilled, (state, action) => {
        state.updateTyreGeneralInfoLoading = false;
        state.updateTyreGeneralInfoData = action.payload?.data || action.payload || null;
        state.updateTyreGeneralInfoError = null;
      })
      .addCase(updateTyreGeneralInfo.rejected, (state, action) => {
        state.updateTyreGeneralInfoLoading = false;
        state.updateTyreGeneralInfoError = action.payload;
      })
      .addCase(fetchTyreDetail.pending, (state) => {
        state.tyreDetailLoading = true;
        state.tyreDetailError = null;
      })
      .addCase(fetchTyreDetail.fulfilled, (state, action) => {
        state.tyreDetailLoading = false;
        state.tyreDetailData = action.payload?.data || action.payload || null;
        state.tyreDetailError = null;
      })
      .addCase(fetchTyreDetail.rejected, (state, action) => {
        state.tyreDetailLoading = false;
        state.tyreDetailData = null;
        state.tyreDetailError = action.payload;
      })
      .addCase(fetchTyreServiceDetail.pending, (state) => {
        state.tyreDetailLoading = true;
        state.tyreDetailError = null;
      })
      .addCase(fetchTyreServiceDetail.fulfilled, (state, action) => {
        state.tyreDetailLoading = false;
        state.tyreDetailData = action.payload?.data || action.payload || null;
        state.tyreDetailError = null;
      })
      .addCase(fetchTyreServiceDetail.rejected, (state, action) => {
        state.tyreDetailLoading = false;
        state.tyreDetailData = null;
        state.tyreDetailError = action.payload;
      })
      .addCase(fetchTyreServiceRecords.pending, (state) => {
        state.tyreServiceRecordsLoading = true;
        state.tyreServiceRecordsError = null;
      })
      .addCase(fetchTyreServiceRecords.fulfilled, (state, action) => {
        const source = action.payload?.data || {};
        state.tyreServiceRecordsLoading = false;
        state.tyreServiceRecordsData = Array.isArray(source)
          ? source
          : Array.isArray(source?.results)
            ? source.results
            : [];
        state.tyreServiceRecordsMeta = source?.meta || {};
        state.tyreServiceRecordsError = null;
      })
      .addCase(fetchTyreServiceRecords.rejected, (state, action) => {
        state.tyreServiceRecordsLoading = false;
        state.tyreServiceRecordsData = [];
        state.tyreServiceRecordsMeta = {};
        state.tyreServiceRecordsError = action.payload;
      })
      .addCase(fetchTyreTrips.pending, (state) => {
        state.tyreTripsLoading = true;
        state.tyreTripsError = null;
      })
      .addCase(fetchTyreTrips.fulfilled, (state, action) => {
        const source = action.payload?.data || {};
        state.tyreTripsLoading = false;
        state.tyreTripsData = Array.isArray(source)
          ? source
          : Array.isArray(source?.results)
            ? source.results
            : [];
        state.tyreTripsMeta = source?.meta || {};
        state.tyreTripsError = null;
      })
      .addCase(fetchTyreTrips.rejected, (state, action) => {
        state.tyreTripsLoading = false;
        state.tyreTripsData = [];
        state.tyreTripsMeta = {};
        state.tyreTripsError = action.payload;
      })
      .addCase(fetchTyreAssignmentHistory.pending, (state) => {
        state.tyreAssignmentHistoryLoading = true;
        state.tyreAssignmentHistoryError = null;
      })
      .addCase(fetchTyreAssignmentHistory.fulfilled, (state, action) => {
        const source = action.payload?.data || {};
        state.tyreAssignmentHistoryLoading = false;
        state.tyreAssignmentHistoryData = Array.isArray(source)
          ? source
          : Array.isArray(source?.results)
            ? source.results
            : [];
        state.tyreAssignmentHistoryMeta = source?.meta || {};
        state.tyreAssignmentHistoryError = null;
      })
      .addCase(fetchTyreAssignmentHistory.rejected, (state, action) => {
        state.tyreAssignmentHistoryLoading = false;
        state.tyreAssignmentHistoryData = [];
        state.tyreAssignmentHistoryMeta = {};
        state.tyreAssignmentHistoryError = action.payload;
      })
      .addCase(fetchTyreHistory.pending, (state) => {
        state.tyreHistoryLoading = true;
        state.tyreHistoryError = null;
      })
      .addCase(fetchTyreHistory.fulfilled, (state, action) => {
        state.tyreHistoryLoading = false;
        state.tyreHistoryData = action.payload?.data || action.payload || [];
        state.tyreHistoryError = null;
      })
      .addCase(fetchTyreHistory.rejected, (state, action) => {
        state.tyreHistoryLoading = false;
        state.tyreHistoryData = [];
        state.tyreHistoryError = action.payload;
      })
      .addCase(commentOnTyre.pending, (state) => {
        state.commentOnTyreLoading = true;
        state.commentOnTyreError = null;
      })
      .addCase(commentOnTyre.fulfilled, (state, action) => {
        state.commentOnTyreLoading = false;
        state.commentOnTyreData = action.payload?.data || action.payload || null;
        state.commentOnTyreError = null;
      })
      .addCase(commentOnTyre.rejected, (state, action) => {
        state.commentOnTyreLoading = false;
        state.commentOnTyreData = null;
        state.commentOnTyreError = action.payload;
      })
      .addCase(deleteTyre.pending, (state) => {
        state.deleteTyreLoading = true;
        state.deleteTyreError = null;
      })
      .addCase(deleteTyre.fulfilled, (state, action) => {
        state.deleteTyreLoading = false;
        state.deleteTyreError = null;
        state.data = state.data.filter((row) => Number(row?.id) !== Number(action.payload?.deletedId));
      })
      .addCase(deleteTyre.rejected, (state, action) => {
        state.deleteTyreLoading = false;
        state.deleteTyreError = action.payload;
      })
      .addCase(fetchTyreBrandsDropdown.fulfilled, (state, action) => {
        state.tyreBrandsDropdownData = action.payload?.data || action.payload || [];
      })
      .addCase(fetchTyreModelsDropdown.fulfilled, (state, action) => {
        state.tyreModelsDropdownData = action.payload?.data || action.payload || [];
      })
      .addCase(fetchTyrePatternsDropdown.fulfilled, (state, action) => {
        state.tyrePatternsDropdownData = action.payload?.data || action.payload || [];
      })
      .addCase(fetchTyreTypesDropdown.fulfilled, (state, action) => {
        state.tyreTypesDropdownData = action.payload?.data || action.payload || [];
      })
      .addCase(fetchTyresDropdown.fulfilled, (state, action) => {
        state.tyresDropdownData =
          action.payload?.data !== undefined ? action.payload.data : action.payload;
      })
      .addCase(fetchAssetTyres.pending, (state, action) => {
        state.assetTyresLoading = true;
        state.assetTyresError = null;
        state.assetTyresData = [];
        state.assetTyresRequest = action.meta.arg || null;
      })
      .addCase(fetchAssetTyres.fulfilled, (state, action) => {
        state.assetTyresLoading = false;
        state.assetTyresError = null;
        state.assetTyresData = Array.isArray(action.payload?.data)
          ? action.payload.data
          : Array.isArray(action.payload)
            ? action.payload
            : [];
        state.assetTyresRequest = action.meta.arg || null;
      })
      .addCase(fetchAssetTyres.rejected, (state, action) => {
        state.assetTyresLoading = false;
        state.assetTyresError = action.payload;
        state.assetTyresData = [];
        state.assetTyresRequest = action.meta.arg || null;
      })
      .addMatcher(
        isAnyOf(
          fetchTyreBrandsDropdown.pending,
          fetchTyreModelsDropdown.pending,
          fetchTyrePatternsDropdown.pending,
          fetchTyreTypesDropdown.pending,
          fetchTyresDropdown.pending
        ),
        (state) => {
          state.dropdownLoadingCount += 1;
          state.dropdownError = null;
        }
      )
      .addMatcher(
        isAnyOf(
          fetchTyreBrandsDropdown.fulfilled,
          fetchTyreModelsDropdown.fulfilled,
          fetchTyrePatternsDropdown.fulfilled,
          fetchTyreTypesDropdown.fulfilled,
          fetchTyresDropdown.fulfilled
        ),
        (state) => {
          state.dropdownLoadingCount = Math.max(0, state.dropdownLoadingCount - 1);
          state.dropdownError = null;
        }
      )
      .addMatcher(
        isAnyOf(
          fetchTyreBrandsDropdown.rejected,
          fetchTyreModelsDropdown.rejected,
          fetchTyrePatternsDropdown.rejected,
          fetchTyreTypesDropdown.rejected,
          fetchTyresDropdown.rejected
        ),
        (state, action) => {
          state.dropdownLoadingCount = Math.max(0, state.dropdownLoadingCount - 1);
          state.dropdownError = action.payload;
        }
      );
  },
});

export const {
  setCurrentPage,
  clearError,
  clearCreateTyreState,
  clearTyreDetailState,
  clearTyreHistoryState,
  clearTyreDropdowns,
} = tyreSlice.actions;

export default tyreSlice.reducer;
